import { useApolloClient } from '@apollo/client';
import { Button, Divider } from '@busybusy/webapp-react-ui';
import { Member } from '__generated__/graphql';
import { MEMBER_NAME_QUERY } from 'apollo/queries/member-queries';
import classNames from 'classnames';
import DetailHeaderWithText from 'components/foundation/DetailHeaderWithText/DetailHeaderWithText';
import {
  MemberMissingCerts,
  useCertificationChecker,
} from 'containers/certification-status-report/hooks/useCertificationChecker';
import { useCostCodeCertificationChecker } from 'containers/certification-status-report/hooks/useCostCodeCertificationChecker';
import { useProjectCertificationChecker } from 'containers/certification-status-report/hooks/useProjectCertificationChecker';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import HeaderDialog from '../HeaderDialog/HeaderDialog';

export interface IMissingCertificationsDialogProps {
  className?: ClassName;
  isOpen: boolean;
  memberIds: string[];
  equipmentIds: string[];
  projectIds: string[];
  costCodeIds: string[];
  onClose: () => void;
  onSubmit?: () => void;
}

export interface IMissingCertificationsObject {
  memberName: string;
  missingCerts: string[];
}

interface IMissingCertificationsDetails {
  equipment: IMissingCertificationsObject[];
  project: IMissingCertificationsObject[];
  costCode: IMissingCertificationsObject[];
}

const MissingCertificationsDialog = (props: IMissingCertificationsDialogProps) => {
  const { className, isOpen, memberIds, equipmentIds, projectIds, costCodeIds, onClose, onSubmit } = props;

  const { getMissingEquipmentCertifications } = useCertificationChecker();
  const { getMissingProjectCertifications } = useProjectCertificationChecker();
  const { getMissingCostCodeCertifications } = useCostCodeCertificationChecker();
  const client = useApolloClient();

  const [missingCertsDetails, setMissinCertsDetails] = useState<IMissingCertificationsDetails>({
    equipment: [],
    project: [],
    costCode: [],
  });

  const fetchMissingCerts = async () => {
    const details: IMissingCertificationsDetails = {
      equipment: [],
      project: [],
      costCode: [],
    };

    if (isOpen && memberIds.length > 0) {
      const memberResults = await client.query<{ members: Member[] }>({
        query: MEMBER_NAME_QUERY,
        variables: { filter: { id: { contains: memberIds } } },
        fetchPolicy: 'network-only',
      });
      const members = memberResults.data.members;

      const [equipmentCerts, projectCerts, costCodeCerts] = await Promise.all([
        getMissingEquipmentCertifications(memberIds, equipmentIds, members),
        getMissingProjectCertifications(members, projectIds),
        getMissingCostCodeCertifications(members, costCodeIds),
      ]);
      details.equipment = equipmentCerts;
      details.project = projectCerts;
      details.costCode = costCodeCerts;
    }

    setMissinCertsDetails(details);
  };

  useEffect(() => {
    fetchMissingCerts();
  }, [
    isOpen,
    memberIds,
    equipmentIds,
    getMissingEquipmentCertifications,
    getMissingProjectCertifications,
    getMissingCostCodeCertifications,
    equipmentIds,
    projectIds,
    costCodeIds,
  ]);

  function renderEquipmentOnlyWarning() {
    return (
      <>
        {t(
          'One or more employees are missing the following certifications required to operate the selected equipment. '
        )}
        {onSubmit && <div className="mt-4">{t('Are you sure you want to continue?')}</div>}
        {missingCertsDetails.equipment.map(({ memberName, missingCerts }, index) => (
          <>
            {missingCerts.length > 0 && (
              <DetailHeaderWithText
                key={index}
                title={memberName}
                value={
                  <ul>
                    {missingCerts.map((cert, index) => (
                      <li key={index}>{cert}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        ))}
      </>
    );
  }

  function renderProjectOnlyWarning() {
    return (
      <>
        {t('One or more employees are missing the following certifications required to clock time to this project. ')}
        {onSubmit && <div className="mt-4">{t('Are you sure you want to continue?')}</div>}
        {missingCertsDetails.project.map(({ memberName, missingCerts }, index) => (
          <>
            {missingCerts.length > 0 && (
              <DetailHeaderWithText
                key={index}
                title={memberName}
                value={
                  <ul>
                    {missingCerts.map((cert, index) => (
                      <li key={index}>{cert}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        ))}
      </>
    );
  }

  function renderCostCodeOnlyWarning() {
    return (
      <>
        {t('One or more employees are missing the following certifications required to clock time to this cost code. ')}
        {onSubmit && <div className="mt-4">{t('Are you sure you want to continue?')}</div>}
        {missingCertsDetails.costCode.map(({ memberName, missingCerts }, index) => (
          <>
            {missingCerts.length > 0 && (
              <DetailHeaderWithText
                key={index}
                title={memberName}
                value={
                  <ul>
                    {missingCerts.map((cert, index) => (
                      <li key={index}>{cert}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        ))}
      </>
    );
  }

  function renderContent() {
    if (
      missingCertsDetails.equipment.length > 0 &&
      missingCertsDetails.project.length === 0 &&
      missingCertsDetails.costCode.length === 0
    ) {
      return renderEquipmentOnlyWarning();
    } else if (
      missingCertsDetails.project.length > 0 &&
      missingCertsDetails.equipment.length === 0 &&
      missingCertsDetails.costCode.length === 0
    ) {
      return renderProjectOnlyWarning();
    } else if (
      missingCertsDetails.costCode.length > 0 &&
      missingCertsDetails.equipment.length === 0 &&
      missingCertsDetails.project.length === 0
    ) {
      return renderCostCodeOnlyWarning();
    } else {
      return (
        <>
          {t(
            `One or more employees are missing the following certifications required to either operate the selected equipment or track time to the selected project or cost code.`
          )}
          {onSubmit && <div className="mt-4">{t('Are you sure you want to continue?')}</div>}
          {missingCertsDetails.equipment.some(({ missingCerts }) => missingCerts.length > 0) && (
            <h3 className="mt-4">{t('Equipment')}</h3>
          )}
          {missingCertsDetails.equipment.map(({ memberName, missingCerts }, index) => (
            <>
              {missingCerts.length > 0 && (
                <DetailHeaderWithText
                  key={index}
                  title={memberName}
                  value={
                    <ul>
                      {missingCerts.map((cert, index) => (
                        <li key={index}>{cert}</li>
                      ))}
                    </ul>
                  }
                />
              )}
            </>
          ))}
          {missingCertsDetails.project.some(({ missingCerts }) => missingCerts.length > 0) && (
            <h3 className="mt-4">{t('Project')}</h3>
          )}
          {missingCertsDetails.project.map(({ memberName, missingCerts }, index) => (
            <>
              {missingCerts.length > 0 && (
                <DetailHeaderWithText
                  key={index}
                  title={memberName}
                  value={
                    <ul>
                      {missingCerts.map((cert, index) => (
                        <li key={index}>{cert}</li>
                      ))}
                    </ul>
                  }
                />
              )}
            </>
          ))}
          {missingCertsDetails.costCode.some(({ missingCerts }) => missingCerts.length > 0) && (
            <h3 className="mt-4">{t('Cost Code')}</h3>
          )}
          {missingCertsDetails.costCode.map(({ memberName, missingCerts }, index) => (
            <>
              {missingCerts.length > 0 && (
                <DetailHeaderWithText
                  key={index}
                  title={memberName}
                  value={
                    <ul>
                      {missingCerts.map((cert, index) => (
                        <li key={index}>{cert}</li>
                      ))}
                    </ul>
                  }
                />
              )}
            </>
          ))}
        </>
      );
    }
  }

  const classes = classNames('missing-certifications-dialog', className);

  return (
    <HeaderDialog
      title={t('Missing Certifications')}
      className={classes}
      isOpen={isOpen}
      onClose={onClose}
      divider={false}
    >
      <div className="mx-8 my-4">{renderContent()}</div>
      <Divider />
      <div className="mx-8 my-4">
        {onSubmit && (
          <Button type="danger" onClick={onSubmit} className="mr-2">
            {t('Continue')}
          </Button>
        )}
        <Button type="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </div>
    </HeaderDialog>
  );
};

export default MissingCertificationsDialog;
